import PropTypes from 'prop-types';
import React, { useCallback, memo } from 'react';
import { Map, fromJS } from 'immutable';

import { BrowseCheckbox } from './BrowseCheckbox';
import { BrowseControlContent } from './BrowseControlContent';
import { Tag } from './Tag';
import { GroupedTags } from './GroupedTags';
import { usePopupContext } from '../../contexts/PopupContext';

const BrowseInputTag = ({
  filter,
  info,
  onChange,
  isBIC,
  submitOnEnter,
  disableStatusChange,
  shouldGroupTags,
  includeSubsidiaries,
  ...restProps
}) => {
  const { openPopup } = usePopupContext();

  const mapTags = useCallback(
    (selecteds, prevList) =>
      selecteds.reduce((accumulator, item) => {
        accumulator[item.id] = {
          value: item.category,
          id: item.id,
          exclude: prevList.has(item.id) ? prevList.getIn([item.id, 'exclude']) : false,
        };

        return accumulator;
      }, {}),
    [],
  );

  const updateListId = useCallback(
    (tagSelecteds, modelSelecteds) => {
      const prevSelectedTags = info.get('selectedTags', Map());
      const prevModelSelecteds = info.get('modelSelecteds', Map());

      onChange({
        filterName: filter.get('name'),
        filterData: {
          selectedTags: fromJS(mapTags(tagSelecteds, prevSelectedTags)),
          selectedModels: fromJS(mapTags(modelSelecteds, prevModelSelecteds)),
        },
      });
    },
    [info, filter],
  );

  const handleTextChange = useCallback(
    event => {
      const { value } = event.target;

      onChange({
        filterName: filter.get('name'),
        filterData: { text: value },
      });
    },
    [filter],
  );

  const handleAddTag = useCallback(
    (displayBIC = true) => {
      const text = info.get('text');
      let selectedList = info.get('selectedList');

      if (text && !selectedList.some(tag => tag.get('value') === text)) {
        selectedList = selectedList.push(new Map({ value: text }));

        onChange({
          filterName: filter.get('name'),
          filterData: { selectedList, text: '' },
        });
      }

      if (displayBIC && isBIC) {
        openPopup('TagsManagementPopup', {
          addNew: false,
          selectedTags: info.get('selectedTags', Map()).toJS(),
          selectedModels: info.get('selectedModels', Map()).toJS(),
          callback: updateListId,
        });
      }
    },
    [info, filter, isBIC, updateListId, openPopup],
  );

  const handleEnterKeyPress = useCallback(
    event => {
      if (event.key === 'Enter' && submitOnEnter === true) {
        handleAddTag(false);
      }
    },
    [submitOnEnter, handleAddTag],
  );

  const handleChangeOperator = useCallback(
    (operator = 'or', excluded = false) => {
      onChange({
        filterName: filter.get('name'),
        filterData: { [excluded ? 'excludeOperator' : 'includeOperator']: operator },
      });
    },
    [filter],
  );

  const handleChangeStatus = useCallback(
    index => {
      const selectedList = info.get('selectedList');

      onChange({
        filterName: filter.get('name'),
        filterData: {
          selectedList: selectedList.setIn([index, 'exclude'], !selectedList.getIn([index, 'exclude'])),
        },
      });
    },
    [info, filter],
  );

  const handleChangeStatusBIC = useCallback(
    (id, name) => {
      const selectedList = info.get(name);

      onChange({
        filterName: filter.get('name'),
        filterData: {
          [name]: selectedList.setIn([id, 'exclude'], !selectedList.getIn([id, 'exclude'])),
        },
      });
    },
    [info, filter],
  );

  const handleRemoveTag = useCallback(
    event => {
      event.stopPropagation();

      const { name } = event.target;
      const tagIndex = parseInt(name.replace('tag', ''), 10);
      let selectedList = info.get('selectedList');

      selectedList = selectedList.delete(tagIndex);

      onChange({
        filterName: filter.get('name'),
        filterData: { selectedList },
      });
    },
    [info, filter],
  );

  const handleRemoveTagBIC = useCallback(
    (event, id, name) => {
      event.stopPropagation();

      let selectedList = info.get(name);

      selectedList = selectedList.delete(id);

      onChange({
        key: name,
        filterName: filter.get('name'),
        filterData: { [name]: selectedList },
      });
    },
    [info, filter],
  );

  const handleIncludeSubsidiariesChange = useCallback(
    event => {
      const { checked } = event.target;

      onChange({
        filterName: filter.get('name'),
        filterData: { includeSubsidiaries: checked },
      });
    },
    [filter],
  );

  const selectedList = info.get('selectedList');
  const excludeOperator = info.get('excludeOperator');
  const includeOperator = info.get('includeOperator');
  const description = filter.get('description') ? <em>{filter.get('description')}</em> : null;
  const allProps = {
    filter,
    info,
    onChange,
    isBIC,
    submitOnEnter,
    disableStatusChange,
    shouldGroupTags,
    includeSubsidiaries,
    ...restProps,
  };

  const tagListContent = selectedList.map((tag, i) => (
    <Tag
      key={i}
      disableStatusChange={disableStatusChange}
      exclude={tag.get('exclude')}
      index={i}
      onChangeStatus={handleChangeStatus}
      onRemoveTag={handleRemoveTag}
      value={tag.get('value')}
    />
  ));

  let industryContent = null;
  let modelContent = null;

  if (isBIC) {
    const plainInfo = info.toJS();

    industryContent = Object.entries(plainInfo.selectedTags || {}).map(([key, value], i) => (
      <Tag
        key={key}
        exclude={value.exclude}
        id={key}
        index={i}
        onChangeStatus={(index, id) => handleChangeStatusBIC(id, 'selectedTags')}
        onRemoveTag={(event, id) => handleRemoveTagBIC(event, id, 'selectedTags')}
        value={value.value}
      />
    ));
    modelContent = Object.entries(plainInfo.selectedModels || {}).map(([key, value], i) => (
      <Tag
        key={key}
        exclude={value.exclude}
        id={key}
        index={i}
        onChangeStatus={(index, id) => handleChangeStatusBIC(id, 'selectedModels')}
        onRemoveTag={(event, id) => handleRemoveTagBIC(event, id, 'selectedModels')}
        value={value.value}
      />
    ));
  }

  return (
    <BrowseCheckbox {...allProps}>
      <BrowseControlContent variant="add">
        {description}
        {shouldGroupTags ? (
          <GroupedTags
            excludeOperator={excludeOperator}
            filterName={filter.get('name')}
            includeOperator={includeOperator}
            onChangeOperator={handleChangeOperator}
            onChangeStatus={handleChangeStatus}
            onRemoveTag={handleRemoveTag}
            selectedList={selectedList}
          />
        ) : (
          tagListContent
        )}
        {industryContent}
        {modelContent}
        <div className="form-inline">
          <div className="form-group input-group">
            <input
              className="form-control"
              onChange={handleTextChange}
              onKeyPress={handleEnterKeyPress}
              type="text"
              value={info.get('text')}
            />
            <span className="input-group-addon" onClick={handleAddTag}>
              <i aria-hidden="true" className="fa fa-plus" />
            </span>
          </div>
          {includeSubsidiaries !== undefined && (
            <>
              <div className="checkbox-wrap">
                <input
                  checked={info.get('includeSubsidiaries')}
                  id="include-subsidiaries-checkbox"
                  onChange={handleIncludeSubsidiariesChange}
                  type="checkbox"
                />
                <label htmlFor="include-subsidiaries-checkbox">Include subsidiaries</label>
              </div>
              {info.get('includeSubsidiaries') && (
                <div>
                  <em>
                    searches description, research notes, deal notes, email log, products, markets, industries, HarvCo
                    tags and Project Scopes of the subsidiaries of those Buyers
                  </em>
                </div>
              )}
            </>
          )}
        </div>
      </BrowseControlContent>
    </BrowseCheckbox>
  );
};

BrowseInputTag.propTypes = {
  disableStatusChange: PropTypes.bool,
  filter: PropTypes.instanceOf(Map).isRequired,
  info: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
  shouldGroupTags: PropTypes.bool,
  submitOnEnter: PropTypes.bool,
};

export default memo(BrowseInputTag);
